import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicesCapabilities.css';
import servicesData from '../data/servicesData';

function Services() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Capabilities — TQNY ALBYANAT";
  }, []);

  const mainServices = servicesData.slice(0, 5);

  return (
    <>
      {/* PAGE HERO */}
      <section className="svc-page-hero">
        <div className="svc-container">
          <nav className="svc-breadcrumb">
            <a href="/" onClick={() => navigate('/')} className="svc-breadcrumb-link">Home</a>
            <span className="svc-breadcrumb-sep">/</span>
            <span className="svc-breadcrumb-current">Capabilities</span>
          </nav>
          <h1 className="svc-page-title">
            Comprehensive Capabilities <br /> for Digital Enterprise Transformation
          </h1>
          <p className="svc-page-subtitle">
            From data strategy and cloud migration to AI enablement, cybersecurity, and enterprise infrastructure, we deliver integrated solutions across nine capability areas, each with proven methodology, global talent, and local expertise.
          </p>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      {mainServices.map((service, idx) => {
        const outcomes = [
          // Service 1: Enterprise Data & AI
          [
            { stat: "200", emphasis: "+", label: "Data sources unified, typical engagement" },
            { stat: "60", emphasis: "%", label: "Faster reporting cycles, average" },
            { stat: "100", emphasis: "%", label: "Regulatory compliance, every project" }
          ],
          // Service 2: Technology & Cloud Services
          [
            { stat: "At", emphasis: "-Globe", label: "Data residency on every engagement" },
            { stat: "3", emphasis: "x", label: "Faster deployment cycles, post-migration" },
            { stat: "40", emphasis: "%", label: "Average cloud cost reduction via FinOps" }
          ],
          // Service 3: Advanced Analytics & AI Enablement
          [
            { stat: "AR", emphasis: "+EN", label: "Native bilingual NLP capability" },
            { stat: "SDAIA", emphasis: "", label: "Aligned with national AI frameworks" },
            { stat: "95", emphasis: "%+", label: "Typical model accuracy on production tasks" }
          ],
          // Service 4: Cloud Acceleration Studio (assumed similar pattern)
          [
            { stat: "Multi", emphasis: "-Cloud", label: "Hyperscaler agnostic deployment" },
            { stat: "60", emphasis: "%", label: "Faster time-to-market for AI workloads" },
            { stat: "SRE", emphasis: "", label: "Managed with SLA guarantees" }
          ],
          // Service 5: Cybersecurity & Compliance
          [
            { stat: "24", emphasis: "/7", label: "SOC monitoring, in-Kingdom" },
            { stat: "100", emphasis: "%", label: "NCA-ECC alignment standard" },
            { stat: "<15", emphasis: " min", label: "Critical incident response time" }
          ]
        ];

        const serviceOutcomes = outcomes[idx] || [];

        return (
          <section
            key={idx}
            className="svc-service-block"
          >
            <div className="svc-container">
              <div
                id={service.slug}
                className={`svc-service-block-grid ${idx % 2 === 1 ? 'svc-service-block-mirror' : ''}`}
              >
                <div className="svc-service-detail">
                  <div className="svc-service-num-large">{String(idx + 1).padStart(2, '0')}</div>
                  <h2 className="svc-service-block-title">{service.title}</h2>
                  <p className="svc-service-block-lead">{service.description}</p>

                  {/* Service Outcomes */}
                  <div className="svc-service-outcomes">
                    {serviceOutcomes.map((outcome, outIdx) => (
                      <div key={outIdx}>
                        <div className="svc-outcome-stat">
                          {outcome.stat}{outcome.emphasis && <em>{outcome.emphasis}</em>}
                        </div>
                        <div className="svc-outcome-label">{outcome.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="svc-service-meta">
                  <div className="svc-service-meta-card">
                    <div className="svc-service-meta-label">What We Deliver</div>
                    <ul className="svc-service-meta-list">
                      {service.subServices.map((sub, subIdx) => (
                        <li key={subIdx}>{sub.title}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* SERVICE 06 - INFRASTRUCTURE PLATFORMS & MANAGED OPERATIONS */}
      <section className="svc-service-block">
        <div className="svc-container">
          <div id="infrastructure-platforms-managed-operations" className="svc-service-block-grid svc-service-block-mirror">
            <div className="svc-service-meta">
              <div className="svc-service-meta-card">
                <div className="svc-service-meta-label">What We Deliver</div>
                <ul className="svc-service-meta-list">
                  <li>Infrastructure & Cloud Architecture Advisory</li>
                  <li>Data Center Design, Migration & Operations</li>
                  <li>Virtualization & Hyperconverged Platforms</li>
                  <li>Linux, Container & Kubernetes Platforms</li>
                  <li>Infrastructure as Code & Automation</li>
                  <li>Storage, Backup & Disaster Recovery</li>
                  <li>Database Administration & High Availability</li>
                  <li>Infrastructure Monitoring & ITIL Service Management</li>
                  <li>Patch, Capacity & Performance Management</li>
                  <li>Resident Engineers & Managed Operations</li>
                </ul>
              </div>
            </div>
            <div className="svc-service-detail">
              <div className="svc-service-num-large">06</div>
              <h2 className="svc-service-block-title">Infrastructure Platforms & Managed Operations</h2>
              <p className="svc-service-block-lead">We design, build, and operate the infrastructure that enterprise applications run on, across data center, private cloud, and public cloud. Our architects and engineers bring hands-on experience across multiple compute, virtualization, Linux, container, storage, and database platforms, and run them under ITIL-aligned managed services, 8x5 or 24x7, depending on the engagement.</p>
              <div className="svc-service-outcomes">
                <div>
                  <div className="svc-outcome-stat">Multi-<em>Vendor</em></div>
                  <div className="svc-outcome-label">Platform-agnostic design and delivery</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">Hybrid</div>
                  <div className="svc-outcome-label">On-prem, private and public cloud</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">8x5<em> · 24x7</em></div>
                  <div className="svc-outcome-label">Coverage matched to the engagement</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE 07 - NETWORKING, SECURITY & UNIFIED COMMUNICATIONS */}
      <section className="svc-service-block">
        <div className="svc-container">
          <div id="networking-security-unified-communications" className="svc-service-block-grid">
            <div className="svc-service-detail">
              <div className="svc-service-num-large">07</div>
              <h2 className="svc-service-block-title">Networking, Security & Unified Communications</h2>
              <p className="svc-service-block-lead">We design and implement secure campus, branch, and data center networks, together with the voice, video, and media services that run on them and the structured cabling beneath them. Our engineers have delivered solutions across multiple vendor platforms, and align every configuration with Saudi national cybersecurity requirements and international cabling standards.</p>
              <div className="svc-service-outcomes">
                <div>
                  <div className="svc-outcome-stat">Multi-<em>Vendor</em></div>
                  <div className="svc-outcome-label">Leading network and security platforms</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">NCA-<em>ECC</em></div>
                  <div className="svc-outcome-label">Controls aligned to national requirements</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">TIA<em>/ISO</em></div>
                  <div className="svc-outcome-label">Cabling tested and certified to standard</div>
                </div>
              </div>
            </div>
            <div className="svc-service-meta">
              <div className="svc-service-meta-card">
                <div className="svc-service-meta-label">What We Deliver</div>
                <ul className="svc-service-meta-list">
                  <li>Network Architecture & Design (LAN, WAN, WLAN)</li>
                  <li>SD-WAN, Data Center Fabric & Wireless</li>
                  <li>Next-Generation Firewall, IPS & VPN</li>
                  <li>Network Access Control & Zero Trust Segmentation</li>
                  <li>Infrastructure Hardening & Vulnerability Management</li>
                  <li>Privileged Access Management</li>
                  <li>IP Telephony, SIP Trunking & Contact Center</li>
                  <li>Video Conferencing & Meeting Rooms</li>
                  <li>IPTV & Digital Signage</li>
                  <li>Structured Cabling, Testing & Certification</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE 08 - MICROSOFT PLATFORMS & MODERN WORKPLACE */}
      <section className="svc-service-block">
        <div className="svc-container">
          <div id="microsoft-platforms-modern-workplace" className="svc-service-block-grid svc-service-block-mirror">
            <div className="svc-service-meta">
              <div className="svc-service-meta-card">
                <div className="svc-service-meta-label">What We Deliver</div>
                <ul className="svc-service-meta-list">
                  <li>Microsoft Platform Architecture & Roadmap</li>
                  <li>Active Directory & Entra ID Design and Migration</li>
                  <li>Microsoft 365 Deployment & Migration</li>
                  <li>SSO, MFA & Conditional Access</li>
                  <li>Windows Server & Exchange Upgrades</li>
                  <li>System Center Configuration & Operations Management</li>
                  <li>Intune Endpoint Management</li>
                  <li>Microsoft 365 Security & Compliance Configuration</li>
                </ul>
              </div>
            </div>
            <div className="svc-service-detail">
              <div className="svc-service-num-large">08</div>
              <h2 className="svc-service-block-title">Microsoft Platforms & Modern Workplace</h2>
              <p className="svc-service-block-lead">We design, deploy, and manage Microsoft server and cloud platforms, from directory services and identity to Microsoft 365 and endpoint management. Our work covers on-premises, hybrid, and cloud-only models, with secure identity and a well-governed, manageable estate as the foundation.</p>
              <div className="svc-service-outcomes">
                <div>
                  <div className="svc-outcome-stat">Hybrid</div>
                  <div className="svc-outcome-label">On-prem, cloud and hybrid identity</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">SSO<em> · MFA</em></div>
                  <div className="svc-outcome-label">Conditional access as standard</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">Lifecycle</div>
                  <div className="svc-outcome-label">Design, migrate and manage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE 09 - ADVISORY & CAPABILITY */}
      <section className="svc-service-block">
        <div className="svc-container">
          <div id="advisory-capability" className="svc-service-block-grid">
            <div className="svc-service-detail">
              <div className="svc-service-num-large">09</div>
              <h2 className="svc-service-block-title">Advisory & Capability.</h2>
              <p className="svc-service-block-lead">Strategic advisory and managed services that deliver outcomes today while building lasting in-country capability for tomorrow.</p>
              <p className="svc-service-block-body">Not every engagement is a build. Sometimes the question is "what should we build?" — or "how do we run what we already have?" Our advisory practice helps clients answer those questions with clarity, then provides the program management, training, and managed services to execute. Knowledge transfer and capability building are standard on every engagement: we want your team to be stronger when we leave than when we arrived.</p>
              <div className="svc-service-outcomes">
                <div>
                  <div className="svc-outcome-stat">100<em>%</em></div>
                  <div className="svc-outcome-label">Engagements include capability transfer</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">SLA</div>
                  <div className="svc-outcome-label">Managed services with SLA guarantees</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">In-KSA</div>
                  <div className="svc-outcome-label">Training delivered on the ground</div>
                </div>
              </div>
            </div>
            <div className="svc-service-meta">
              <div className="svc-service-meta-card">
                <div className="svc-service-meta-label">What We Deliver</div>
                <ul className="svc-service-meta-list">
                  <li>Strategic technology & digital advisory</li>
                  <li>Technology roadmap & target architecture</li>
                  <li>Transformation program management</li>
                  <li>Capability assessments & gap analysis</li>
                  <li>Managed services & SLA agreements</li>
                  <li>Training, mentoring & knowledge transfer</li>
                  <li>Saudi Talent recruitment & placement</li>
                  <li>Centers of Excellence (CoE) setup</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH SECTION */}
      <section className="svc-approach">
        <div className="svc-container">
          <div className="svc-section-eyebrow">How We Work</div>
          <h2 className="svc-approach-title">A method <em>that delivers.</em></h2>
          <p className="svc-approach-intro">Every engagement follows a deliberate four-phase rhythm — built to surface risk early, deliver value continuously, and leave behind capability that lasts.</p>
          <div className="svc-approach-grid">
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">01</div>
              <div className="svc-approach-step-title">Listen</div>
              <div className="svc-approach-step-desc">We start by understanding the business — not just the technology. Stakeholder workshops, current-state assessment, and outcome alignment.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">02</div>
              <div className="svc-approach-step-title">Design</div>
              <div className="svc-approach-step-desc">Target architecture, delivery roadmap, success metrics. We write down what we'll build, why, and how we'll know it worked.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">03</div>
              <div className="svc-approach-step-title">Deliver</div>
              <div className="svc-approach-step-desc">Iterative delivery in tight cycles. Working software in weeks, not quarters. Continuous feedback, no surprise reveals.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">04</div>
              <div className="svc-approach-step-title">Transfer</div>
              <div className="svc-approach-step-desc">Knowledge transfer is built into every phase. When we hand off, your team owns the system, the documentation, and the playbook.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="svc-cta" id="contact">
        <div className="svc-container">
          <div className="svc-cta-inner">
            <div className="svc-cta-content">
              <div className="svc-cta-eyebrow">Start a Conversation</div>
              <h2>Let's build something that lasts.</h2>
              <p>Every great engagement begins with a conversation. Tell us about your ambition — we'll tell you how we can help.</p>
              <button onClick={() => navigate("/contact")} type="submit" className="svc-btn-cta-primary">Request a Meeting <span>→</span></button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;